import { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { DataTableContainer } from 'components/DataTable/UI'
import FbButton from 'components/FbUI/FbButton'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import type { Lead, PaginatedLeadsResponse } from '../sequences_api'

const PAGE_SIZE = 25

const VERIFICATION_STATUS_LABELS: Record<string, string> = {
  verified: 'Verified',
  unverified: 'Unverified',
  risky: 'Risky',
  bounced: 'Bounced',
  unsubscribed: 'Unsubscribed',
  unknown: 'Unknown',
  verifying: 'Verifying',
  inactive: 'Inactive',
}

const VERIFICATION_STATUS_COLORS: Record<string, string> = {
  verified: 'bg-green-100 text-green-800',
  unverified: 'bg-gray-100 text-gray-800',
  risky: 'bg-yellow-100 text-yellow-800',
  bounced: 'bg-red-100 text-red-800',
  unsubscribed: 'bg-red-100 text-red-800',
  unknown: 'bg-gray-100 text-gray-800',
  verifying: 'bg-blue-100 text-blue-800',
  inactive: 'bg-gray-100 text-gray-800',
}

interface LeadsTableProps {
  data: PaginatedLeadsResponse | undefined
  page: number
  setPage: (page: number) => void
  onSync: () => void
  isSyncing: boolean
  isLoading: boolean
}

const columnHelper = createColumnHelper<Lead>()

function LeadsTableContent({
  data,
  page,
  setPage,
  onSync,
  isSyncing,
  isLoading,
}: LeadsTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.accessor('full_name', {
        header: 'Lead',
        size: 250,
        cell: (info) => (
          <div>
            <p className="text-sm font-medium text-[#191D23] truncate">
              {info.getValue()}
            </p>
            <p className="text-sm text-[#6E6E6E]">{info.row.original.email}</p>
          </div>
        ),
      }),
      columnHelper.accessor('verification_status', {
        header: 'Status',
        size: 120,
        cell: (info) => {
          const status = info.getValue()
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                VERIFICATION_STATUS_COLORS[status] ||
                VERIFICATION_STATUS_COLORS.unknown
              }`}
            >
              {VERIFICATION_STATUS_LABELS[status] || status}
            </span>
          )
        },
      }),
      columnHelper.accessor('emails_sent', {
        header: 'Sent',
        size: 80,
        cell: (info) => (
          <span className="text-sm text-[#6E6E6E]">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('unique_opens', {
        header: 'Opens',
        size: 80,
        cell: (info) => (
          <span className="text-sm text-[#6E6E6E]">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('unique_replies', {
        header: 'Replies',
        size: 80,
        cell: (info) => (
          <span className="text-sm text-[#6E6E6E]">{info.getValue()}</span>
        ),
      }),
    ],
    []
  )

  return (
    <DataTableContainer>
      <DataTable
        tableKey="leads-table"
        data={data?.results ?? []}
        columns={columns}
        tableHeader={
          <div className="flex justify-end items-center w-full py-2">
            <FbButton
              onClick={onSync}
              disabled={isSyncing}
              loading={isSyncing}
              variant="secondary"
              size="sm"
            >
              Sync Contacts
            </FbButton>
          </div>
        }
        isPaginationEnabled={true}
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isLoading,
        }}
        loading={isLoading}
      />
    </DataTableContainer>
  )
}

export function LeadsTable(props: LeadsTableProps) {
  return (
    <ColumnsStoreProvider tableKey="leads-table">
      <DataTableProvider tableKey="leads-table">
        <LeadsTableContent {...props} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
